import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calculator, TrendingUp, Target, Users, Briefcase, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ROICalculator({ eventId, eventName }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [profile, setProfile] = useState({
    email: '',
    name: '',
    job_title: '',
    industry: '',
    company_size: '',
    seniority_level: '',
    primary_goals: [],
    interests: []
  });

  const goals = [
    { id: 'networking', label: 'Networking', icon: Users },
    { id: 'learning', label: 'Learning', icon: Target },
    { id: 'deals', label: 'Business Deals', icon: Briefcase },
    { id: 'recruiting', label: 'Recruiting', icon: Users },
    { id: 'speaking', label: 'Speaking/Visibility', icon: TrendingUp }
  ];

  const toggleGoal = (goalId) => {
    setProfile(prev => ({
      ...prev,
      primary_goals: prev.primary_goals.includes(goalId)
        ? prev.primary_goals.filter(g => g !== goalId)
        : [...prev.primary_goals, goalId]
    }));
  };

  const calculateROI = async () => {
    if (!profile.email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/genome/predict-roi`, {
        event_id: eventId,
        user_profile: profile
      });

      setPrediction(response.data.prediction);
      setStep(3);
    } catch (error) {
      toast.error('Failed to calculate ROI');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Calculator className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-bold">ROI Predictor</h3>
            <p className="text-green-100 text-sm">Personalized value estimate for {eventName}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-gray-600">Tell us about yourself to get a personalized ROI prediction.</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Email</label>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={profile.email}
                  onChange={e => setProfile({ ...profile, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Name</label>
                <Input
                  placeholder="Your name"
                  value={profile.name}
                  onChange={e => setProfile({ ...profile, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Job Title</label>
                <Input
                  placeholder="e.g. Marketing Director"
                  value={profile.job_title}
                  onChange={e => setProfile({ ...profile, job_title: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Industry</label>
                <Input
                  placeholder="e.g. Technology"
                  value={profile.industry}
                  onChange={e => setProfile({ ...profile, industry: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Company Size</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={profile.company_size}
                  onChange={e => setProfile({ ...profile, company_size: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="startup">Startup (1-50)</option>
                  <option value="sme">SME (51-500)</option>
                  <option value="enterprise">Enterprise (500+)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Seniority Level</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={profile.seniority_level}
                  onChange={e => setProfile({ ...profile, seniority_level: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid-Level</option>
                  <option value="senior">Senior</option>
                  <option value="c-suite">C-Suite/Executive</option>
                </select>
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full">
              Next: Select Your Goals
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-gray-600">What are your main goals for attending this event?</p>

            <div className="grid grid-cols-2 gap-3">
              {goals.map(goal => {
                const Icon = goal.icon;
                const isSelected = profile.primary_goals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    onClick={() => toggleGoal(goal.id)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className={`font-medium ${isSelected ? 'text-green-700' : 'text-gray-700'}`}>
                      {goal.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button
                onClick={calculateROI}
                disabled={loading || profile.primary_goals.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Calculate My ROI'}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && prediction && (
          <div className="space-y-6">
            {/* Main ROI Display */}
            <div className="text-center">
              <p className="text-gray-500 mb-2">Your Predicted ROI</p>
              <div className="text-5xl font-bold text-green-600">
                £{prediction.predicted_roi_gbp.toLocaleString()}
              </div>
              <p className="text-gray-500 text-sm mt-2">
                Range: £{prediction.predicted_roi_range.low.toLocaleString()} - £{prediction.predicted_roi_range.high.toLocaleString()}
              </p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                prediction.confidence_level === 'high'
                  ? 'bg-green-100 text-green-700'
                  : prediction.confidence_level === 'medium'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
              }`}>
                {prediction.confidence_level.toUpperCase()} Confidence
              </span>
            </div>

            {/* Multipliers */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Value Multipliers</p>
              <div className="space-y-2">
                {Object.entries(prediction.multipliers).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-gray-600 capitalize">{key.replace('_', ' ')}</span>
                    <span className={`font-medium ${value >= 1 ? 'text-green-600' : 'text-orange-600'}`}>
                      {value}x
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Recommendations</p>
              <ul className="space-y-2">
                {prediction.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-green-500">✓</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>

            <Button variant="outline" onClick={() => { setStep(1); setPrediction(null); }} className="w-full">
              Recalculate
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}